import React from 'react';
import { SearchX } from 'lucide-react';

import frontImg from '../assets/images/front.png';
import frontRightImg from '../assets/images/front_right.png';
import rightImg from '../assets/images/right.png';
import rearRightImg from '../assets/images/rear_right.png';
import rearImg from '../assets/images/rear.png';
import rearLeftImg from '../assets/images/rear_left.png';
import leftImg from '../assets/images/left.png';
import frontLeftImg from '../assets/images/front_left.png';

/**
 * The artwork behind "show, don't tell" on the photo step.
 *
 * Each upload slot displays one of these rather than describing the shot in
 * words: "one from each corner" means little, a little car outline means it
 * immediately. They are the same silhouettes the camera overlays with, so what
 * the customer is asked for and what they are guided to take visibly match.
 */

export const ANGLE_SILHOUETTES = [
  { key: 'Front', src: frontImg },
  { key: 'Front Right', src: frontRightImg },
  { key: 'Right', src: rightImg },
  { key: 'Rear Right', src: rearRightImg },
  { key: 'Rear', src: rearImg },
  { key: 'Rear Left', src: rearLeftImg },
  { key: 'Left', src: leftImg },
  { key: 'Front Left', src: frontLeftImg }
];

/** One walk-around angle. */
export const AngleIllustration = ({ src, alt }) => (
  <img src={src} alt={alt} className="h-full w-full object-contain opacity-45" />
);

/** A close-up of the damage — the silhouette with a "look here" mark on it. */
export const DamageIllustration = () => (
  <span className="relative flex h-full w-full items-center justify-center">
    <img src={frontLeftImg} alt="" aria-hidden="true" className="h-full w-full object-contain opacity-25" />
    <SearchX className="absolute h-7 w-7 text-[var(--brand-accent)]" />
  </span>
);

/**
 * A simplified New Zealand registration ticket — the windscreen sticker.
 *
 * Drawn rather than photographed so it is legible at thumbnail size and carries
 * no real vehicle's details. It shows the three things that identify the
 * ticket: the plate number, the expiry, and the vehicle it belongs to.
 */
export const RegoTicket = ({ className = '', filled = true }) => (
  <svg viewBox="0 0 120 96" className={className} role="img" aria-label="Registration ticket">
    {filled && <rect x="2" y="2" width="116" height="92" rx="8" className="fill-background" />}
    <rect
      x="2" y="2" width="116" height="92" rx="8"
      className="fill-none stroke-current" strokeWidth="3" opacity="0.6"
    />

    {/* Plate number — the largest thing on a real ticket */}
    <rect x="14" y="14" width="92" height="26" rx="4" className="fill-current" opacity="0.12" />
    <text
      x="60" y="33"
      textAnchor="middle"
      className="fill-current"
      style={{ fontSize: '18px', fontWeight: 700, letterSpacing: '1px' }}
    >
      ABC123
    </text>

    {/* Expiry */}
    <text x="14" y="58" className="fill-current" opacity="0.7" style={{ fontSize: '9px' }}>EXPIRES</text>
    <text x="14" y="72" className="fill-current" style={{ fontSize: '13px', fontWeight: 600 }}>31 MAR</text>

    {/* Vehicle */}
    <text x="70" y="58" className="fill-current" opacity="0.7" style={{ fontSize: '9px' }}>VEHICLE</text>
    <text x="70" y="70" className="fill-current" style={{ fontSize: '10px', fontWeight: 600 }}>TOYOTA</text>
    <text x="70" y="82" className="fill-current" opacity="0.7" style={{ fontSize: '9px' }}>COROLLA</text>
  </svg>
);

/** The same ticket as a camera framing guide — outline only, over the video. */
export const RegoTicketOverlay = () => (
  <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-8">
    <RegoTicket filled={false} className="h-auto w-full max-w-sm text-white opacity-70" />
  </div>
);
